import './AgentChatHistory.css'
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { Button } from '@/components/action/Button'
import { MarkdownViewer } from '@/components/scalar/MarkdownViewer'
import { AgentReply } from './AgentReply'
import type { ChatMessage } from './agentChatMessages'

type Props = {
  history: ChatMessage[]
  setHistory: Dispatch<SetStateAction<ChatMessage[]>>
  sending: boolean
  onStop: () => void
  setArticleIds?: (articleIds?: string[]) => void
  onRefreshEditor?: () => void
  /** Increment to force pin-to-bottom (eg after starting a new conversation). */
  pinToBottomToken?: number
}

export function AgentChatHistory({
  history,
  setHistory,
  sending,
  onStop,
  setArticleIds,
  onRefreshEditor,
  pinToBottomToken = 0,
}: Props) {

  const [autoScroll, setAutoScroll] = useState(true)
  const chatHistoryRef = useRef<HTMLDivElement>(null)
  const chatContentRef = useRef<HTMLDivElement>(null)
  const autoScrollRef = useRef(autoScroll)
  const programmaticScrollRef = useRef(false)

  autoScrollRef.current = autoScroll

  //--------------------------------------------------------------------------------
  function scrollChatToBottom() {
    const el = chatHistoryRef.current
    if (!el) return
    programmaticScrollRef.current = true
    el.scrollTop = el.scrollHeight
    // Ignore scroll events from this adjustment (and a follow-up layout pass).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        programmaticScrollRef.current = false
      })
    })
  }

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (!autoScroll) return
    scrollChatToBottom()
  }, [history, autoScroll])

  //--------------------------------------------------------------------------------
  // Markdown/widgets can grow the reply after `history` settles; keep pinned while
  // auto-scroll is on.
  useEffect(() => {
    const content = chatContentRef.current
    if (!content) return

    const observer = new ResizeObserver(() => {
      if (autoScrollRef.current) scrollChatToBottom()
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [])

  //--------------------------------------------------------------------------------
  function handleChatScroll() {
    if (programmaticScrollRef.current) return
    const el = chatHistoryRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40
    setAutoScroll(nearBottom)
  }

  //--------------------------------------------------------------------------------
  // Pin to bottom when a new send starts, or when the parent requests it
  // (new conversation, etc.).
  useEffect(() => {
    if (sending) setAutoScroll(true)
  }, [sending])

  useEffect(() => {
    if (pinToBottomToken === 0) return
    setAutoScroll(true)
  }, [pinToBottomToken])

  //--------------------------------------------------------------------------------
  return (
    <div
      className='AgentChatHistory'
      ref={chatHistoryRef}
      onScroll={handleChatScroll}
    >
      <div className='Content' ref={chatContentRef}>
        {history.map(message => {
          const isActiveStream =
            sending
            && message.role === 'agent'
            && message.id === history[history.length - 1]?.id

          return (
            <div
              key={message.id}
              className={`Message ${message.role === 'user' ? 'User' : 'Agent'}`}
            >
              {message.role === 'agent' ? (
                <div className='AgentTurn'>
                  <AgentReply
                    content={message.content || (isActiveStream ? '…' : '')}
                    widgets={message.widgets}
                    reasoning={message.reasoning}
                    streaming={isActiveStream}
                    reasoningStreaming={isActiveStream && !message.content}
                    fromStream={message.fromStream}
                    onStreamConsumed={() => setHistory(prev => prev.map(entry =>
                      entry.id === message.id && entry.fromStream
                        ? { ...entry, fromStream: undefined }
                        : entry,
                    ))}
                    setArticleIds={setArticleIds}
                    onRefreshEditor={onRefreshEditor}
                  />
                  {isActiveStream && (
                    <Button
                      variant='text'
                      className='Stop'
                      label='Stop'
                      icon='times'
                      onClick={onStop}
                    />
                  )}
                </div>
              ) : (
                <div className='Bubble'>
                  <MarkdownViewer value={message.content} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
